/**
 * Type selection identity — the string key the workbench selects declared
 * types by.
 *
 * Types are space-scoped in the engine (registry key `<space>::<name>` —
 * "two types collide only when they share a space AND a name"), so two
 * spaces may each legally declare `type module gizmo`. A bare name cannot
 * identify one of them; the selection key mirrors the engine's registry
 * form. Consumers that need the as-written name (matching a module's
 * `typeName`, which is always bare) strip the space with `bareTypeName`.
 * Bare keys remain accepted everywhere via `findTypeBySelection`'s
 * fallback — old persisted picks and description links address types bare
 * (r56).
 */

export function typeSelectionKey(t: {
  readonly name: string;
  readonly space?: string;
}): string {
  return t.space ? `${t.space}::${t.name}` : t.name;
}

/** The as-written (space-less) name of a selection key. */
export function bareTypeName(key: string): string {
  const i = key.lastIndexOf("::");
  return i >= 0 ? key.slice(i + 2) : key;
}

/** Resolve a selection key to its type: exact key first, bare-name
 *  first-match fallback (bare picks are inherently ambiguous across
 *  same-named space-scoped types — the fallback keeps them landing). */
export function findTypeBySelection<
  T extends { readonly name: string; readonly space?: string },
>(types: readonly T[], key: string | null | undefined): T | null {
  if (!key) return null;
  return (
    types.find((t) => typeSelectionKey(t) === key) ??
    types.find((t) => t.name === bareTypeName(key)) ??
    null
  );
}
